// How big the character is on an outdoor scene, and where her feet are. A scene that
// draws people at real-world scale sets `scene.worldScale` (OUTDOOR_CHARACTER_SCALE) and
// optionally `scene.perspective` (a profile for carCatalog's perspectiveScale — the street's
// by default). A room keeps worldScale 1 and sets only `scene.perspective` (roomPerspective.mjs):
// the flat 240-px art at the bottom of the screen, smaller towards the back wall. Without
// either, everything here collapses to the flat art.
// Pure: takes any object with those two fields, so `node --test` covers the conversions.
import { CHARACTER_ART_H, PERSPECTIVE, perspectiveScale, centreYFromGround, groundYFromCentre } from './carCatalog.mjs';

const HALF_ART = CHARACTER_ART_H / 2;

const profileOf = (scene) => scene.perspective ?? PERSPECTIVE;
const flat = (scene) => (scene.worldScale ?? 1) === 1 && !scene.perspective;

/** The scale NatalkaSprite should be drawn at when her feet are on `feetY`. */
export function characterScaleAtFeet(scene, feetY) {
  const ws = scene.worldScale ?? 1;
  return flat(scene) ? 1 : ws * perspectiveScale(feetY, profileOf(scene));
}

/** Sprite-centre y for feet on `feetY` (the inverse of feetForCentre). */
export function centreForFeet(scene, feetY) {
  const ws = scene.worldScale ?? 1;
  return flat(scene) ? feetY - HALF_ART : centreYFromGround(HALF_ART * ws, feetY, profileOf(scene));
}

/** Where the feet are for a sprite whose centre is at `centreY`. */
export function feetForCentre(scene, centreY) {
  const ws = scene.worldScale ?? 1;
  return flat(scene) ? centreY + HALF_ART : groundYFromCentre(HALF_ART * ws, centreY, profileOf(scene));
}

/** The scale for a sprite whose centre is at `centreY` (feet worked back out first). */
export function characterScaleAtCentre(scene, centreY) {
  return characterScaleAtFeet(scene, feetForCentre(scene, centreY));
}

/**
 * Per-frame: size the walking character for where she stands and keep the walker's floor
 * maths in step (its floor lines are FEET, so it needs the current sprite height).
 */
export function fitCharacter(scene, natalka = scene.natalka, walker = scene.walker) {
  // On a sofa / in a bed she is drawn at the furniture's size (FurnitureSeats), which has no perspective.
  if (flat(scene) || !natalka || natalka.pose) return;
  const s = characterScaleAtCentre(scene, natalka.y);
  natalka.setWorldScale(s);
  walker?.setSpriteHeight(CHARACTER_ART_H * s);
}
